import { OpsDashboardApi, IUserProviderState } from "@jem/components";
import { postRequest, getRequest, putRequest } from "@jem/components";

export interface CalendarUploadResult {
  success: boolean;
  message: string;
  requestId: string;
  status: string;
  errors: string[];
  summary?: {
    jemEventsInserted: number;
    ihccJemDataInserted: number;
    ihccPaymentOrdersInserted: number;
    jemFiscalMonthCreated: boolean;
    calndr02FiscalMonthJemDataCreated: boolean;
    calndr02FiscalMonthMasterDataCreated: boolean;
  };
}

export interface CalendarRequestInfo {
  requestId: string;
  fiscalYear: number;
  createdBy: string;
  createdDate: string;
  status: string;
  approvedBy: string;
  reviewDate: string;
  runDate: string;
  jemEventCount: number;
  ihccEventCount: number;
}

export async function uploadCalendarFile(
  configuration: OpsDashboardApi,
  getTokenFn: IUserProviderState["accessToken"],
  file: File
): Promise<CalendarUploadResult | null> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.calendarUpload}`;
  const formData = new FormData();
  formData.append("file", file);
  return postRequest<CalendarUploadResult>(endpoint, formData, getTokenFn, {});
}

export async function approveCalendarRequest(
  configuration: OpsDashboardApi,
  getTokenFn: IUserProviderState["accessToken"],
  requestId: string
): Promise<CalendarUploadResult | null> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.calendarApprove.replace("{requestId}", requestId)}`;
  return putRequest<CalendarUploadResult>(endpoint, {}, getTokenFn);
}

export async function rejectCalendarRequest(
  configuration: OpsDashboardApi,
  getTokenFn: IUserProviderState["accessToken"],
  requestId: string,
  comments: string
): Promise<string | null> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.calendarReject.replace("{requestId}", requestId)}`;
  return putRequest<string>(endpoint, comments, getTokenFn);
}

export async function getCalendarRequests(
  configuration: OpsDashboardApi,
  getTokenFn: IUserProviderState["accessToken"]
): Promise<CalendarRequestInfo[] | null> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.calendarRequests}`;
  return getRequest<CalendarRequestInfo[]>(endpoint, getTokenFn);
}

export interface CalendarEventRow {
  fiscalYear: number;
  fiscalMonth: number;
  eventId: number;
  eventName: string;
  date: string;
}

export interface CalendarRequestDetail {
  fiscalYear: number;
  jemEvents: CalendarEventRow[];
  ihccEvents: CalendarEventRow[];
}

export interface CalendarSqlPreview {
  requestId: string;
  sql: string;
}

export async function getCalendarRequestDetail(
  configuration: OpsDashboardApi,
  getTokenFn: IUserProviderState["accessToken"],
  requestId: string
): Promise<CalendarRequestDetail | null> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.calendarRequests}/${requestId}`;
  return getRequest<CalendarRequestDetail>(endpoint, getTokenFn);
}

export async function getCalendarSqlPreview(
  configuration: OpsDashboardApi,
  getTokenFn: IUserProviderState["accessToken"],
  requestId: string
): Promise<CalendarSqlPreview | null> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.calendarSqlPreview.replace("{requestId}", requestId)}`;
  return getRequest<CalendarSqlPreview>(endpoint, getTokenFn);
}

export function getCalendarTemplateUrl(
  configuration: OpsDashboardApi,
  fiscalYear: number
): string {
  return `${configuration.baseApiUrl}${configuration.endpoints.calendarTemplate.replace("{fiscalYear}", fiscalYear.toString())}`;
}
